import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Badge, Box, Button, Flex, Heading, IconButton, Switch, Tabs, Text } from '@radix-ui/themes'
import { Cross2Icon, PlusIcon } from '@radix-ui/react-icons'
import { Ops } from '@willie/schemas'
import { TabState } from '~/components/TabState'
import { AssemblyProfilesTab } from '~/features/installation-profiles/components/AssemblyProfilesTab'
import { InstallationProfileDialog } from '~/features/installation-profiles/components/InstallationProfileDialog'
import { InstallationProfilesTable } from '~/features/installation-profiles/components/InstallationProfilesTable'
import { MeterMakersTab } from '~/features/installation-profiles/components/MeterMakersTab'
import { SkuCatalogTab } from '~/features/installation-profiles/components/SkuCatalogTab'
import { TabHeader } from '~/features/installation-profiles/components/TabHeader'
import {
  HARDWARE_TABS,
  isAssemblyProfile,
  hardwareTabLabel,
  toSection,
  type HardwareTab
} from '~/features/installation-profiles/constants'
import { profileAssembly, type InstallationProfileRow } from '~/features/installation-profiles/assembly'
import { useTRPC } from '~/utils/trpc'

interface HardwareSearch {
  section?: Exclude<HardwareTab, 'profiles'>
  supportedOnly?: true
  noAssemblyProfile?: true
  assembly?: Ops.InstallationProfileSkus.AssemblyProfile
}

const matchesFilters =
  ({ supportedOnly, noAssemblyProfile, assembly }: HardwareSearch) =>
  (profile: InstallationProfileRow): boolean =>
    (!supportedOnly || profile.supported) &&
    (!noAssemblyProfile || profileAssembly(profile).kind === 'missing') &&
    (!assembly || Ops.InstallationProfileSkus.installationProfileAssemblyProfile(profile.slug) === assembly)

interface FilterSwitchProps {
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}

const FilterSwitch = ({ label, checked, onCheckedChange }: FilterSwitchProps) => (
  <Text as="label" size="2" color="gray">
    <Flex align="center" gap="2">
      <Switch size="1" checked={checked} onCheckedChange={onCheckedChange} />
      {label}
    </Flex>
  </Text>
)

interface DialogState {
  open: boolean
  key: number
  profile: InstallationProfileRow | null
}

const HardwarePage = () => {
  const trpc = useTRPC()
  const navigate = useNavigate({ from: Route.fullPath })
  const search = Route.useSearch()
  const section = search.section ?? 'profiles'
  const [dialog, setDialog] = useState<DialogState>({ open: false, key: 0, profile: null })

  const profilesQuery = useQuery(trpc.ops.installationProfiles.list.queryOptions())
  const profiles = profilesQuery.data ?? []
  const visibleProfiles = profiles.filter(matchesFilters(search))

  const setSearch = (patch: Partial<HardwareSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const openDialog = (profile: InstallationProfileRow | null) =>
    setDialog((current) => ({ open: true, key: current.key + 1, profile }))

  return (
    <Flex direction="column" gap="4">
      <Heading size="6">Hardware</Heading>

      <Tabs.Root value={section} onValueChange={(next) => setSearch({ section: toSection(next) })}>
        <Tabs.List>
          {HARDWARE_TABS.map((option) => (
            <Tabs.Trigger key={option} value={option}>
              {hardwareTabLabel[option]}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Box pt="4">
          <Tabs.Content value="profiles">
            <Flex direction="column" gap="3">
              <TabHeader
                action={
                  <Button variant="soft" onClick={() => openDialog(null)}>
                    <PlusIcon />
                    New profile
                  </Button>
                }
              >
                Profiles narrow the installation picker from known meter and logger hardware. Supported profiles
                are the ones we commit to installing.
              </TabHeader>
              <Flex align="center" justify="end" wrap="wrap" gap="4">
                {search.assembly ? (
                  <Badge color="gray" variant="soft" size="2">
                    <span className="font-mono">{search.assembly}</span>
                    <IconButton
                      size="1"
                      variant="ghost"
                      color="gray"
                      aria-label="Clear assembly profile filter"
                      onClick={() => setSearch({ assembly: undefined })}
                    >
                      <Cross2Icon />
                    </IconButton>
                  </Badge>
                ) : null}
                <FilterSwitch
                  label="No assembly profile"
                  checked={search.noAssemblyProfile ?? false}
                  onCheckedChange={(checked) => setSearch({ noAssemblyProfile: checked || undefined })}
                />
                <FilterSwitch
                  label="Supported only"
                  checked={search.supportedOnly ?? false}
                  onCheckedChange={(checked) => setSearch({ supportedOnly: checked || undefined })}
                />
              </Flex>
              <TabState
                loading={profilesQuery.isLoading}
                error={profilesQuery.error?.message ?? null}
                isEmpty={profiles.length === 0}
                emptyMessage="No installation profiles yet."
              >
                {visibleProfiles.length === 0 ? (
                  <Text color="gray">No installation profiles match these filters.</Text>
                ) : (
                  <InstallationProfilesTable profiles={visibleProfiles} onEdit={openDialog} />
                )}
              </TabState>
            </Flex>
          </Tabs.Content>

          <Tabs.Content value="assembly">
            <TabState loading={profilesQuery.isLoading} error={profilesQuery.error?.message ?? null}>
              <AssemblyProfilesTab profiles={profiles} />
            </TabState>
          </Tabs.Content>

          <Tabs.Content value="skus">
            <SkuCatalogTab />
          </Tabs.Content>

          <Tabs.Content value="makers">
            <MeterMakersTab />
          </Tabs.Content>
        </Box>
      </Tabs.Root>

      <InstallationProfileDialog
        key={dialog.key}
        open={dialog.open}
        onOpenChange={(open) => setDialog((current) => ({ ...current, open }))}
        profile={dialog.profile}
      />
    </Flex>
  )
}

const isTruthyFlag = (value: unknown): true | undefined => (value === true || value === 'true' ? true : undefined)

export const Route = createFileRoute('/installation-profiles/')({
  component: HardwarePage,
  validateSearch: (raw: Record<string, unknown>): HardwareSearch => ({
    section: toSection(raw.section),
    supportedOnly: isTruthyFlag(raw.supportedOnly),
    noAssemblyProfile: isTruthyFlag(raw.noAssemblyProfile),
    assembly: isAssemblyProfile(raw.assembly) ? raw.assembly : undefined
  })
})
